import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link } from "react-router-dom";
import { notify } from "utils/notify";
import {
  AuthContainer,
  AuthSidebar,
  LogoContainer,
  LogoSeparator,
  BrandLogo,
  AnniversaryLogo,
  AuthTitle,
  AuthContent,
  PoolProLogo,
  SidebarFormContainer,
  SidebarFormTitle,
  AuthDescription,
  FormGroup,
  Label,
  Input,
  ErrorMessage,
  Button,
  AuthLink,
  LinkRow,
} from "styles/auth.styles";
import daveyLogo from "assets/Davey_logotype_Yellow.svg";
import anniversaryLogo from "assets/90_years_logo_White.svg";
import poolProfessionalLogo from "assets/PoolProfessional_STACK_Yellow_White.png";
import { forgotPassword } from "services/authService";

type ForgotPasswordFormData = {
  email: string;
};

const schema = yup.object().shape({
  email: yup
    .string()
    .email("Please enter a valid email address")
    .required("Email is required"),
});

const ForgotPassword: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsSubmitting(true);
    try {
      await forgotPassword(data.email);
      notify.success(
        "Password reset instructions have been sent to your email."
      );
    } catch (error: any) {
      notify.error(
        error.response?.data?.message ||
          "An error occurred. Please try again later."
      );
      console.error("Password reset request error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthContainer>
      <AuthSidebar>
        <LogoContainer>
          <BrandLogo src={daveyLogo} alt="Davey Logo" />
          <LogoSeparator />
          <AnniversaryLogo src={anniversaryLogo} alt="90 Years Logo" />
        </LogoContainer>
        <AuthTitle>PoolProfessional</AuthTitle>

        <SidebarFormContainer>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <SidebarFormTitle>Forgot Password</SidebarFormTitle>
            <AuthDescription>
              Enter your email address and we&apos;ll send you instructions to
              reset your password.
            </AuthDescription>

            <FormGroup>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                autoComplete="email"
                {...register("email")}
                disabled={isSubmitting}
              />
              {errors.email && (
                <ErrorMessage>{errors.email.message}</ErrorMessage>
              )}
            </FormGroup>

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Sending..." : "Send Reset Instructions"}
            </Button>

            <LinkRow>
              <AuthLink as={Link} to="/auth/signin">
                Back to Login
              </AuthLink>
            </LinkRow>
          </form>
        </SidebarFormContainer>
      </AuthSidebar>

      <AuthContent>
        <PoolProLogo
          src={poolProfessionalLogo}
          alt="Davey Pool Professional Logo"
        />
      </AuthContent>
    </AuthContainer>
  );
};

export default ForgotPassword;
